import React from "react";
import styles from "./styles.module.scss";
import { PrimitiveProps } from "@helpers/interfaces/primitive";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { Image } from "@components/Image/Image";
import { ImageField } from "@helpers/interfaces/image";
import { Link } from "@components/Link/Link";
import { Text } from "@components/Text/Text";
import classnames from "classnames";
import { Icon } from "@components/Icon/Icon";
import playIcon from "@svg-icons/play.component.svg";
import { AspectRatio } from "@components/AspectRatio/AspectRatio";
import { Box } from "@components/Box/Box";
import { VideoField } from "@helpers/interfaces/media";
import { UmbracoVideo } from "@components/UmbracoVideo/UmbracoVideo";
const getArticleFontSize = (size?: string): string => {
  return styles[`article-box--fontsize-${size}`];
};
const getSectionVariant = (sectionVariant?: string): string => {
  return styles[`article-box--section-${sectionVariant}`];
};
export interface ArticleBoxProps extends PrimitiveProps {
  image?: ImageField | string;
  video?: VideoField;
  section?: string;
  title?: string;
  url?: string;
  sectionVariant?: string;
  fontSize?: string;
  imageWidth?: number[];
  imageHeight?: number[];
  showPlayIcon?: boolean;
  ratio?: string | string[];
  withLightbox?: boolean;
  openModal?: () => void;
}
const TextContent: React.FC<ArticleBoxProps> = ({
  section,
  title,
  sectionVariant // xsmall, small
}) => {
  if (sectionVariant && sectionVariant === "medium") {
    return (
      <>
        {section && <Text className={styles.sectionText}>{section} {" "}</Text>}
        <Text className={styles.teaserText}>{title}</Text>
      </>
    );
  } else {
    return (
      <Text>
        {section && (
          <Text as="span" className={styles.sectionText}>
            {section} {" "}
          </Text>
        )}
        <Text as="span" className={styles.teaserText}>{` ${title}`}</Text>
      </Text>
    );
  }
};
const Content: React.FC<ArticleBoxProps> = ({
  image,
  video,
  section,
  title,
  sectionVariant, // xsmall, small
  imageWidth,
  imageHeight,
  showPlayIcon,
  ratio,
  withLightbox = false
}) => {
  
  return (
    <>
      <AspectRatio
        ratio={ratio ?? "16:9"}
        className={classnames(
          sectionVariant && styles[`article-box--section-${sectionVariant}-img`]
        )}
      >
        <>
          {video && <UmbracoVideo video={video} />}
          {image && !video && imageWidth && imageHeight ? (
            <>
              {typeof image === "string" ? (
                <Image src={image} />
              ) : (
                <UmbracoImage
                  image={image}
                  width={imageWidth}
                  height={imageHeight}
                  params={!withLightbox ? { mode: "crop" } : undefined}
                  isCover={withLightbox ? false : true}
                  isContain={withLightbox}
                />
              )}
              {showPlayIcon && (
                <Icon svg={playIcon} className={styles.playIcon} />
              )}
            </>
          ) : (
            <Box className={styles.noImage}>
              <TextContent
                section={section}
                title={title}
                sectionVariant={sectionVariant}
              />
            </Box>
          )}
        </>
      </AspectRatio>
      {(image || video) && (
        <TextContent
          section={section}
          title={title}
          sectionVariant={sectionVariant}
        />
      )}
    </>
  );
};
const ArticleBox: React.FC<ArticleBoxProps> = ({
  image,
  video,
  section,
  title,
  url,
  sectionVariant, // xsmall, small
  fontSize, // xsmall, small, large, xlarge
  imageWidth,
  imageHeight,
  showPlayIcon,
  ratio,
  withLightbox,
  openModal,
  ...props
}) => {
  if (url) {
    return (
      <Link
        href={url}
        {...props}
        className={classnames(
          styles.articleBox,
          fontSize && getArticleFontSize(fontSize),
          sectionVariant && getSectionVariant(`${sectionVariant}`),
          !image &&
            sectionVariant &&
            getSectionVariant(`${sectionVariant}--noimg`)
        )}
      >
        <Content
          image={image}
          video={video}
          section={section}
          title={title}
          sectionVariant={sectionVariant}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          showPlayIcon={showPlayIcon}
          ratio={ratio}
        />
      </Link>
    );
  } else if (withLightbox) {
    return (
      <Box
        {...props}
        className={classnames(
          styles.articleBox,
          styles.withLightbox,
          fontSize && getArticleFontSize(fontSize),
          sectionVariant && getSectionVariant(`${sectionVariant}`),
          !image &&
            sectionVariant &&
            getSectionVariant(`${sectionVariant}--noimg`)
        )}
        onClick={() => {
          withLightbox && openModal && openModal();
        }}
      >
        <Content
          image={image}
          video={!withLightbox ? video : undefined}
          section={section}
          title={title}
          sectionVariant={sectionVariant}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          showPlayIcon={showPlayIcon}
          ratio={ratio}
          withLightbox={withLightbox}
        />
      </Box>
    );
  } else {

    return (
      <Box
        {...props}
        className={classnames(
          styles.articleBox,
          fontSize && getArticleFontSize(fontSize),
          sectionVariant && getSectionVariant(`${sectionVariant}`),
          !image &&
            sectionVariant &&
            getSectionVariant(`${sectionVariant}--noimg`)
        )}
        onClick={() => {
          withLightbox && openModal && openModal();
        }}
      >
        <Content
          image={image}
          video={!withLightbox ? video : undefined}
          section={section}
          title={title}
          sectionVariant={sectionVariant}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          showPlayIcon={showPlayIcon}
          ratio={ratio}
        />
      </Box>
    );
  }
};
ArticleBox.defaultProps = {
  imageWidth: [300, 400, 500, 600],
  imageHeight: [169, 255, 281, 337]
};
export { ArticleBox };